import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import api from '../../lib/api';
import { GraduationCap, Plus, Trash2, Eye, EyeOff, Loader2, Copy, Clock, Download, Mail } from 'lucide-react';

const fmtDuration = (s) => {
  if (!s) return '0m';
  const h = Math.floor(s / 3600), m = Math.round((s % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m}m`;
};
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const CollegeAccessTab = () => {
  const [codes, setCodes] = useState([]);
  const [analytics, setAnalytics] = useState({ summary: [], sessions: [] });
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ label: '', code: '' });
  const [saving, setSaving] = useState(false);
  const [showCodes, setShowCodes] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, a, e] = await Promise.all([
        api.get('/college/admin/codes'), api.get('/college/admin/analytics'), api.get('/college/admin/enquiries'),
      ]);
      setCodes(c.data); setAnalytics(a.data); setEnquiries(e.data);
    } catch { toast.error('Failed to load college data'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const createCode = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/college/admin/codes', form);
      toast.success(`Access code created for ${form.label}`);
      setForm({ label: '', code: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create code'); }
    finally { setSaving(false); }
  };

  const toggle = async (c) => {
    try { await api.patch(`/college/admin/codes/${c.id}`); load(); }
    catch { toast.error('Failed to update'); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete the access code for "${c.label}"? They will no longer be able to log in.`)) return;
    try { await api.delete(`/college/admin/codes/${c.id}`); load(); toast.success('Code deleted'); }
    catch { toast.error('Failed to delete'); }
  };

  const genCode = () => {
    const s = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
    let out = 'UPA-';
    for (let i = 0; i < 8; i++) out += s[Math.floor(Math.random() * s.length)];
    setForm(f => ({ ...f, code: out }));
  };

  const copyLink = () => {
    navigator.clipboard.writeText('https://www.upaschool.com/college-partners');
    toast.success('Presentation link copied');
  };

  const statFor = (codeId) => analytics.summary.find(s => s.code_id === codeId);

  if (loading) return <div className="h-40 bg-[#111] rounded-xl animate-pulse" />;

  return (
    <div className="space-y-6" data-testid="college-access-tab">
      <Card className="bg-[#111] border-[#333]">
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <div className="w-10 h-10 bg-[#7000FF] rounded-lg flex items-center justify-center"><GraduationCap className="w-5 h-5 text-white" /></div>
            <div className="flex-1 min-w-[200px]">
              <p className="text-white font-medium">UPA College — Private Presentation Access</p>
              <p className="text-sm text-white/50">Create a named code per partner, share the link, and track engagement.</p>
            </div>
            <Button size="sm" variant="outline" className="border-[#333] text-white" onClick={copyLink} data-testid="college-copy-link">
              <Copy className="w-4 h-4 mr-1" />Copy page link
            </Button>
            <Button size="sm" variant="outline" className="border-[#333] text-white" onClick={() => setShowCodes(s => !s)} data-testid="college-show-codes">
              {showCodes ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}{showCodes ? 'Hide codes' : 'Show codes'}
            </Button>
          </div>

          <form onSubmit={createCode} className="flex flex-wrap gap-3 p-4 bg-[#0a0a0a] rounded-lg border border-dashed border-[#333]">
            <Input required placeholder="Who is this for? (e.g. MK Council — Creative Team)" value={form.label}
              onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
              className="flex-1 min-w-[220px] bg-[#111] border-[#333] text-white" data-testid="college-code-label-input" />
            <div className="flex gap-2 flex-1 min-w-[220px]">
              <Input required placeholder="Access code (min 6 chars)" value={form.code}
                onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                className="bg-[#111] border-[#333] text-white" data-testid="college-code-value-input" />
              <Button type="button" variant="outline" className="border-[#333] text-white shrink-0" onClick={genCode} data-testid="college-generate-code">Generate</Button>
            </div>
            <Button type="submit" disabled={saving} className="bg-[#FF007F] hover:bg-[#d60068] text-white" data-testid="college-create-code">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}Create code
            </Button>
          </form>
        </CardContent>
      </Card>

      <div className="space-y-3">
        {codes.length === 0 && (
          <Card className="bg-[#111] border-[#333]"><CardContent className="py-10 text-center text-white/50">
            No access codes yet — create one above, then share the page link and code with your partner.
          </CardContent></Card>
        )}
        {codes.map(c => {
          const stat = statFor(c.id);
          return (
            <Card key={c.id} className="bg-[#111] border-[#333]" data-testid={`college-code-${c.id}`}>
              <CardContent className="pt-5 pb-4">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white font-semibold">{c.label}</p>
                      <Badge className={c.active ? 'bg-emerald-600 text-white' : 'bg-gray-600 text-white'}>{c.active ? 'active' : 'revoked'}</Badge>
                    </div>
                    <p className="text-white/50 text-sm mt-1 font-mono">{showCodes ? c.code : '••••••••••'}</p>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-white/60">
                    <span className="flex items-center gap-1.5"><Eye className="w-4 h-4 text-[#FF007F]" />{stat?.visits || 0} visit{(stat?.visits || 0) === 1 ? '' : 's'}</span>
                    <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#7000FF]" />{fmtDuration(stat?.total_seconds)}</span>
                    <span className="flex items-center gap-1.5"><Download className="w-4 h-4 text-[#FFD700]" />{stat?.downloads || 0}</span>
                    <span className="text-xs text-white/40">last: {fmtDate(stat?.last_visit)}</span>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="border-[#333] text-white" onClick={() => toggle(c)} data-testid={`college-toggle-${c.id}`}>
                      {c.active ? 'Revoke' : 'Re-activate'}
                    </Button>
                    <Button size="icon" variant="ghost" className="text-red-400" onClick={() => remove(c)} data-testid={`college-delete-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {analytics.sessions.length > 0 && (
        <Card className="bg-[#111] border-[#333]">
          <CardContent className="pt-6">
            <p className="text-white font-medium mb-4">Recent visits</p>
            <div className="space-y-2">
              {analytics.sessions.slice(0, 15).map(s => (
                <div key={s.id} className="flex flex-wrap items-center gap-3 text-sm py-2 border-b border-[#222] last:border-0">
                  <span className="text-white font-medium flex-1 min-w-[140px]">{s.label}</span>
                  <span className="text-white/50">{fmtDate(s.started_at)}</span>
                  <span className="text-white/50 flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{fmtDuration(s.duration_seconds)}</span>
                  {s.downloads > 0 && <Badge className="bg-[#FFD700]/20 text-[#FFD700] border border-[#FFD700]/40">deck downloaded</Badge>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {enquiries.length > 0 && (
        <Card className="bg-[#111] border-[#333]">
          <CardContent className="pt-6">
            <p className="text-white font-medium mb-4 flex items-center gap-2"><Mail className="w-4 h-4 text-[#FF007F]" />Partnership enquiries ({enquiries.length})</p>
            <div className="space-y-3">
              {enquiries.map(e => (
                <div key={e.id} className="p-3 bg-[#0a0a0a] rounded-lg border border-[#333]">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-white font-semibold">{e.name}</p>
                    {e.organisation && <span className="text-white/50 text-sm">· {e.organisation}</span>}
                    {e.area && <Badge className="bg-[#7000FF]/20 text-[#b57aff] border border-[#7000FF]/40">{e.area}</Badge>}
                    <span className="text-white/40 text-xs ml-auto">{fmtDate(e.created_at)} · via {e.access_label}</span>
                  </div>
                  <p className="text-white/60 text-sm mt-1">{e.email}</p>
                  {e.message && <p className="text-white/50 text-sm mt-1 whitespace-pre-wrap">{e.message}</p>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CollegeAccessTab;
